import React, { useState, useContext, useEffect } from "react";
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { MobXProviderContext } from "mobx-react";
import { toJS } from "mobx";
import { InfoButton, RadioActive, RadioInActive } from "assets/img";
import InputError from "components/InputError/InputError";
import { stripePromise } from "utils/helpers/stripe";
import { upTrack, UPEVENTS } from "utils/helpers/userPilot";

const CheckoutForm = ({
  setModal,
  rightClick,
  isCheckout,
  handleTemporaryCard,
  handlePaymentMethod,
  setPaymentMethodAdded,
  visible,
  isPlaceOrder,
  newGroupOrder,
  handleTemporaryCardId,
  marketplace,
  setDefaultCard,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState(null);
  const stores = useContext(MobXProviderContext);
  const userStore = toJS(stores?.userStore);
  let { defaultLocation } = userStore;
  const [saveCard, setSaveCard] = useState(true);

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
      focus: {
        color: "#303238",
        backgroundColor: "#000",
      },
    },
  };

  useEffect(() => {
    if (visible) {
      elements?.getElement(CardNumberElement)?.clear();
      elements?.getElement(CardExpiryElement)?.clear();
      elements?.getElement(CardCvcElement)?.clear();
      setSaveCard(true);
      setErrorMessage(null);
    }
  }, [visible]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardNumberElement);
    if (newGroupOrder && !saveCard) {
      try {
        const response = await stores.paymentInfoStore.createSetupIntent({
          location_id: defaultLocation?.organizationId,
        });

        const client_secret = response?.client_secret;
        const result = await stripe.confirmCardSetup(client_secret, {
          payment_method: {
            card: cardElement,
          },
        });

        const responseCard =
          await stores.paymentInfoStore.getTemporaryCreditCards(
            result?.setupIntent?.payment_method,
            { location_id: defaultLocation?.organizationId }
          );

        handleTemporaryCardId(responseCard);
      } catch (err) {
        setErrorMessage("Failed to set up card.");
        return;
      }
      setModal(false);
    } else {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      let cardDetails = {
        display_brand: paymentMethod?.card?.brand,
        last4: paymentMethod?.card?.last4,
        exp_month: paymentMethod?.card?.exp_month,
        exp_year: paymentMethod?.card?.exp_year,
      };

      if (error) {
        setErrorMessage(error.message);
      } else if (!saveCard && (isCheckout || isPlaceOrder)) {
        handleTemporaryCard(cardDetails);
        stores.paymentInfoStore.saveCardElements(cardElement);

        if (isCheckout || isPlaceOrder) handlePaymentMethod();
        setModal(false);
      } else {
        const cardId = paymentMethod?.id;

        stores.paymentInfoStore
          .createLocationCreditCard({
            location_id: defaultLocation?.organizationId,
            card_id: cardId,
          })
          .then((response) => {
            upTrack(UPEVENTS.CARD_ADDED);
            stores.paymentInfoStore.updateDefaultLocationCreditCards(
              response?.data[0]?.id,
              {
                location_id: defaultLocation?.organizationId,
                default: true,
              }
            );
            if (marketplace) setDefaultCard(true);

            if (isCheckout) setPaymentMethodAdded(true);
            stores.userStore?.setLoader(false);
            if (isCheckout || isPlaceOrder) handlePaymentMethod();
            else rightClick();
            return response;
          })
          .catch(() => stores.userStore.setLoader(false));

        setModal(false);
      }
      return;
    }
  };

  return (
    <form
      className="d-col items-center w-full h-full gap-6 justify-between items-center"
      onSubmit={handleSubmit}
    >
      <div className="w-full gap-4 flex-col flex">
        <div className="d-col w-full gap-4">
          <div className="text-slate-700 text-lg font-inter-medium leading-snug">
            Card Number
          </div>

          <div
            style={{
              padding: "15px",
            }}
            className="w-full bg-slate-50 rounded-xl border border-slate-50"
          >
            <CardNumberElement
              options={{
                ...CARD_ELEMENT_OPTIONS,
                placeholder: "1234 5678 9101 1121",
              }}
            />
          </div>
        </div>

        <div className="lg:d-row md:d-col w-full gap-[18px]">
          <div className="d-col w-full gap-4">
            <div className="text-slate-700 text-lg font-inter-medium leading-snug">
              Expiration Date
            </div>

            <div
              style={{
                padding: "15px",
              }}
              className="w-full bg-slate-50 rounded-xl border border-slate-50"
            >
              <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
            </div>
          </div>

          <div className="d-col w-full gap-4">
            <div className="text-slate-700 text-lg font-inter-medium leading-snug">
              CVV{" "}
            </div>

            <div
              style={{
                padding: "15px",
              }}
              className="w-full bg-slate-50 rounded-xl border border-slate-50"
            >
              <CardCvcElement
                options={{ ...CARD_ELEMENT_OPTIONS, placeholder: "123" }}
              />
            </div>
          </div>
        </div>

        {(isCheckout || isPlaceOrder) && (
          <div className="d-row w-full gap-[18px] mt-[24px]">
            <div className="d-col w-full gap-4">
              <div className="text-slate-700 text-lg font-inter-medium leading-snug">
                Would you like to save this credit card to your account?
              </div>

              {!isPlaceOrder && (
                <div className="w-full p-2 bg-zinc-100 rounded-lg justify-center items-center gap-3 inline-flex">
                  <img
                    src={InfoButton}
                    alt="info-icon"
                    width={16}
                    height={16}
                  />

                  <div className="grow shrink basis-0 text-slate-700 text-base font-inter-normal leading-tight">
                    Saved credit cards will be available to all individuals with
                    administrative privileges when placing an order.
                  </div>
                </div>
              )}

              <div
                className="flex cursor-pointer"
                onClick={() => setSaveCard(false)}
              >
                <img
                  alt="radio-button"
                  src={!saveCard ? RadioActive : RadioInActive}
                />

                <small className="text-sm text-black font-inter-normal ml-4">
                  No, use it only for this order.
                </small>
              </div>

              <div
                className="flex cursor-pointer"
                onClick={() => setSaveCard(true)}
              >
                <img
                  alt="radio-button"
                  src={saveCard ? RadioActive : RadioInActive}
                />

                <small className="text-sm text-black font-inter-normal ml-4">
                  Yes, save it to my payment methods
                </small>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-2 inline-flex gap-[19px]">
        <button
          className="btn-light btn-style lg:w-[98px] md:w-[197.50px]"
          type="button"
          onClick={() => {
            setModal(false);
          }}
        >
          Cancel
        </button>

        <button
          className="btn-purple btn-style lg:w-[98px] md:w-[197.50px]"
          type="submit"
          disabled={!stripe}
        >
          Save
        </button>
      </div>

      {errorMessage && <InputError error={errorMessage} />}
    </form>
  );
};

const AddCard = ({
  visible,
  setModal,
  leftClick,
  rightClick,
  isCheckout,
  handleTemporaryCard,
  handlePaymentMethod,
  setPaymentMethodAdded,
  isPlaceOrder,
  newGroupOrder,
  handleTemporaryCardId,
  marketplace,
  setDefaultCard,
}) => (
  <div
    className={`fixed inset-0 z-[90] flex items-center justify-center bg-black bg-opacity-25 ${
      visible ? "visible opacity-100" : "invisible opacity-0"
    } transition-opacity duration-300`}
  >
    <div className="fixed inset-0" />

    <div className="relative w-[664px] bg-white rounded-xl shadow-xl p-8">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-2xl font-bold text-[#3a3a3a]">Add New Card</h2>
        <button
          onClick={() => setModal(false)}
          className="text-dark-gray text-3xl"
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <Elements stripe={stripePromise}>
        <CheckoutForm
          setModal={setModal}
          leftClick={leftClick}
          rightClick={rightClick}
          isCheckout={isCheckout}
          handleTemporaryCard={handleTemporaryCard}
          handlePaymentMethod={handlePaymentMethod}
          setPaymentMethodAdded={setPaymentMethodAdded}
          visible={visible}
          isPlaceOrder={isPlaceOrder}
          newGroupOrder={newGroupOrder}
          handleTemporaryCardId={handleTemporaryCardId}
          marketplace={marketplace}
          setDefaultCard={setDefaultCard}
        />
      </Elements>
    </div>
  </div>
);

export default AddCard;
